import React, { useEffect, useMemo, useState } from 'react';

import type { FlowModel } from '../types';
import { fetchFlowModels, fetchToolkitCategories } from '../services/api';
import { useGenContext } from '../contexts/GenContext';
import { useAppContext } from '../contexts/AppContext';

const ToolkitPage: React.FC = () => {
    const { applyToolkit: onApplyToolkit } = useGenContext();
    const { isAuthenticated } = useAppContext();
    const [toolkits, setToolkits] = useState<FlowModel[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>('全部');
    const [categoryNames, setCategoryNames] = useState<string[]>([]);

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchFlowModels().then(setToolkits).catch(console.error);
        fetchToolkitCategories().then(setCategoryNames).catch(console.error);
    }, [isAuthenticated]);

    const categories = useMemo(() => ['全部', ...categoryNames], [categoryNames]);

    // FloatingAssistant 事件監聽
    useEffect(() => {
        const handleFilterCategory = (e: Event) => {
            const { category } = (e as CustomEvent<{ category: string }>).detail;
            if (categories.includes(category)) setActiveCategory(category);
        };
        const handleApplyTemplate = (e: Event) => {
            const { templateId } = (e as CustomEvent<{ templateId: string }>).detail;
            const item = toolkits.find(t => t.id === templateId);
            if (item) onApplyToolkit(item);
        };
        window.addEventListener('assistant:toolkit:filter-category', handleFilterCategory);
        window.addEventListener('assistant:toolkit:apply-template', handleApplyTemplate);
        return () => {
            window.removeEventListener('assistant:toolkit:filter-category', handleFilterCategory);
            window.removeEventListener('assistant:toolkit:apply-template', handleApplyTemplate);
        };
    }, [categories, toolkits, onApplyToolkit]);

    const filteredToolkits = activeCategory === '全部'
        ? toolkits
        : toolkits.filter(t => t.category === activeCategory);

    return (
        <div className="space-y-6 md:space-y-8 animate-in slide-in-from-bottom-4 duration-500">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* 手機隱藏主標題（已由頂部列顯示） */}
                <div className="hidden lg:block">
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white mb-1">里務宣傳工具包</h2>
                    <p className="text-xs text-slate-400 font-bold tracking-widest uppercase">TEMPLATES & VISUAL ASSETS</p>
                </div>

                {/* Category Filters — 手機可橫向捲動，避免擠壓 */}
                <div className="flex bg-white dark:bg-slate-900 rounded-full p-1 shadow-sm border border-slate-200 dark:border-slate-800 overflow-x-auto max-w-full">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors whitespace-nowrap ${activeCategory === cat
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredToolkits.map(item => (
                    <div key={item.id} onClick={() => onApplyToolkit(item)}
                        className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] hover:border-blue-500/50 hover:shadow-xl transition-all cursor-pointer overflow-hidden flex flex-col"
                    >
                        {/* Top Image Area — 列表場景用後端縮圖代理 */}
                        {(() => {
                            const displayImg = item.imageThumbnailUrl || item.image || '';
                            const isUrl = displayImg.startsWith('http') || displayImg.startsWith('/');
                            return (
                                <div
                                    className={`h-48 w-full relative p-4 bg-cover bg-center ${!isUrl ? (item.image || 'bg-slate-100 dark:bg-slate-800') : 'bg-slate-100 dark:bg-slate-800'}`}
                                    style={isUrl ? { backgroundImage: `url(${displayImg})` } : undefined}
                                >
                            {/* Tag Pill */}
                            {/* <div className="bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-bold px-3 py-1 rounded-full inline-block shadow-sm">
                                {item.tag}
                            </div> */}
                                </div>
                            );
                        })()}

                        {/* Bottom Content Area */}
                        <div className="p-6 text-center space-y-2 flex-grow bg-white dark:bg-slate-900 flex flex-col justify-center">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{item.title}</h3>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400">{item.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ToolkitPage;
